import { useEffect, useState } from "react";

import { SupportCase, SupportCaseList } from "../models/supportCases";
import { fetchSupportJson, SupportRequestError } from "./supportCases";

type SupportCasesState = Readonly<{
  data?: SupportCaseList;
  /** HTTP status of a failed load (0 for network errors). */
  errorStatus?: number;
  isLoading: boolean;
  isLoadingMore?: boolean;
}>;

// Pages loaded so far, shared by every caller and kept across route changes so
// the list neither flashes a spinner nor drops scrolled-in pages.
let cached: SupportCasesState | undefined;
let loadedPages = 0;
const listeners = new Set<(state: SupportCasesState) => void>();

const update = (state: SupportCasesState) => {
  cached = state;
  listeners.forEach(listener => listener(state));
};

/** `first` in order, then the cases from `rest` it doesn't already contain. */
export const mergeSupportCases = (
  first: ReadonlyArray<SupportCase>,
  rest: ReadonlyArray<SupportCase>
) => {
  const ids = new Set(first.map(supportCase => supportCase.caseId));
  return [...first, ...rest.filter(supportCase => !ids.has(supportCase.caseId))];
};

/** Appends the next page of cases; no-op while loading or when every page is loaded. */
export const loadMoreSupportCases = () => {
  if (!cached?.data?.hasMore || cached.isLoadingMore) {
    return;
  }
  update({ ...cached, isLoadingMore: true });
  const page = loadedPages + 1;
  fetchSupportJson<SupportCaseList>(`/cases?page=${page}`)
    .then(next => {
      loadedPages = page;
      update({
        isLoading: false,
        data: { ...next, cases: mergeSupportCases(cached?.data?.cases ?? [], next.cases) },
      });
    })
    // Stop paging on failure so the scroll sentinel doesn't retry in a loop.
    .catch(
      () => cached?.data && update({ isLoading: false, data: { ...cached.data, hasMore: false } })
    );
};

export const useSupportCases = (enabled = true): SupportCasesState => {
  const [state, setState] = useState<SupportCasesState>(() => cached ?? { isLoading: enabled });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    listeners.add(setState);
    if (!cached?.data) {
      setState({ isLoading: true });
    }
    // Refresh page 1 and keep any later pages already scrolled in.
    fetchSupportJson<SupportCaseList>("/cases")
      .then(first => {
        const previous = loadedPages > 1 ? cached?.data : undefined;
        loadedPages ||= 1;
        update({
          isLoading: false,
          isLoadingMore: cached?.isLoadingMore,
          data: previous
            ? {
                ...first,
                hasMore: previous.hasMore,
                cases: mergeSupportCases(first.cases, previous.cases),
              }
            : first,
        });
      })
      .catch((error: unknown) => {
        update({
          errorStatus: error instanceof SupportRequestError ? error.status : 0,
          isLoading: false,
        });
        cached = undefined;
        loadedPages = 0;
      });
    return () => {
      listeners.delete(setState);
    };
  }, [enabled]);

  return state;
};
